import { Container, Graphics, Text } from "pixi.js"

import { DEFAULT_FONT_FAMILY, TEXT_ROLES } from "../text/typography"
import { CLOSE_BUTTON_RADIUS, CloseButton } from "./close_button"

// A moving world anchor with a screen-clamped label. Lifetime counts visible time,
// so walking off screen pauses the lesson rather than silently completing it.
export class RecruitMenuHint extends Container {
    private leader = new Graphics()
    private panel = new Container()
    private background = new Graphics()
    private label = new Text("", { fontFamily: DEFAULT_FONT_FAMILY, fontSize: TEXT_ROLES.caption.fontSize, fill: 0xffffff, lineHeight: 18 })
    private closeButton: CloseButton
    private dismissed = false
    private elapsed = 0
    targetId?: string

    constructor(onPress?: () => void, private onShown?: (entityId: string) => void) {
        super()
        this.addChild(this.leader, this.panel)
        this.closeButton = new CloseButton({ onClick: this.dismiss })
        this.panel.addChild(this.background, this.label, this.closeButton)
        this.panel.interactive = true
        this.panel.on("pointerdown", (event: any) => {
            event.stopPropagation()
            onPress?.()
        })
        this.label.position.set(10, 8)
        this.visible = false
    }

    dismiss = () => {
        this.dismissed = true
        this.reset()
    }

    reset = () => {
        this.visible = false
        this.targetId = undefined
        this.elapsed = 0
        // Dismissal survives room changes; only changing player resets the lesson.
    }

    resetSession = () => {
        this.reset()
        this.dismissed = false
    }

    follow = (targetId: string | undefined, point: { x: number; y: number } | null,
        screen: { w: number; h: number }, touch: boolean, deltaMs: number) => {
        this.visible = false
        if (!targetId) this.targetId = undefined
        if (this.dismissed || !targetId || !point) return
        const firstAppearance = !this.targetId
        if (firstAppearance) {
            this.elapsed = 0
        }
        this.targetId = targetId
        this.elapsed += deltaMs
        if (this.elapsed >= 24000) {
            this.dismiss()
            return
        }
        this.visible = true
        this.label.text = touch ? "Touch and hold a recruit\nto feed them or give orders."
            : "Right-click a recruit for actions.\nTouchpad: tap with two fingers."
        const closeSpace = 2 * CLOSE_BUTTON_RADIUS + 16
        this.label.style.wordWrap = true
        this.label.style.wordWrapWidth = Math.max(80, screen.w - closeSpace - 36)
        const width = Math.min(this.label.width + 20 + closeSpace, screen.w - 16)
        const height = Math.max(this.label.height + 16, 2 * CLOSE_BUTTON_RADIUS + 12)
        this.closeButton.position.set(width - CLOSE_BUTTON_RADIUS - 6, CLOSE_BUTTON_RADIUS + 6)
        this.background.clear().lineStyle(1, 0xff0000).beginFill(0x171313, 0.96)
            .drawRoundedRect(0, 0, width, height, 5).endFill()
        const bounds = this.panel.getLocalBounds()
        this.panel.position.set(
            Math.max(8 - bounds.x, Math.min(screen.w - bounds.x - bounds.width - 8, point.x - width / 2)),
            Math.max(8 - bounds.y, Math.min(screen.h - bounds.y - bounds.height - 8, point.y - height - 48)),
        )
        this.leader.clear().lineStyle(2, 0xff0000, 0.85)
            .moveTo(this.panel.x + width / 2, this.panel.y + height)
            .lineTo(point.x, point.y - 12).drawCircle(point.x, point.y - 12, 5)
        if (firstAppearance) this.onShown?.(targetId)
    }
}
